import { ReactElement, useEffect, useRef, useState } from "react";
import {
  IGroupValidationResult,
  Input,
  InputGroup,
  QuestionTooltipProps,
  required,
  requiredNonEmptyString,
  WatchedFunction,
  QuestionTooltip,
  StatusMessage,
} from "component-library";
import { MediaShowreelIcon, MicIcon } from "assets";
import { Trans, useTranslation } from "i18n";
import { dataTestIds } from "data-testids";
import {
  IUploaderContent,
  UploaderMapper,
} from "mediaModules/media/components/UploaderWrapper";
import { IFileUploadComplete } from "mediaModules/media/TitaniumUploader/Models";
import {
  MediaAssetType,
  MediaMetadataPayload,
  MediaStorageSpace,
  SUPPORTED_AUDIO_EXTENSIONS,
  SUPPORTED_VIDEO_EXTENSIONS,
} from "mediaModules/media/models";
import { UploadCapacityProgress } from "mediaModules/media/components/UploadCapacityProgress";
import "./AddMediaForm.scss";

export const VALIDATE = new WatchedFunction(() => undefined);
const TITLE_MAX_LENGTH = 255;
const UPLOADER_ACCEPT_EXTENSIONS = [
  ...SUPPORTED_VIDEO_EXTENSIONS,
  ...SUPPORTED_AUDIO_EXTENSIONS,
]
  .map((ext) => `.${ext}`)
  .join(",");

export interface IAddMediaFormProps {
  uploaderContent: IUploaderContent;
  titleRequiredError: string;
  mediaRequiredError: string;
  titleLabel: string;
  titlePlaceholder: string;
  titleTooltip: QuestionTooltipProps;
  metadataPayload: MediaMetadataPayload;
  updateMetadata: (metadataPayload: MediaMetadataPayload) => void;
  showDeletePopup: (
    metadataPayload: MediaMetadataPayload,
    callback: () => void
  ) => void;
  saveChanges: () => void;
  children: (submit: () => void) => ReactElement | null;
  storageSpaceByType: Record<MediaAssetType, MediaStorageSpace>;
  disableSaveButton: (isFileUploading: boolean) => void;
  preUploadValidation?: (
    durationInMs: number,
    mediaType?: MediaAssetType
  ) => boolean;
}

export interface IAddMediaFormState {
  title: string;
  mediaInfo: string;
  isFormSubmitted: boolean;
  validationData: {
    title: {
      invalid: boolean;
      validationMessage: string;
    };
    mediaInfo: {
      invalid: boolean;
      validationMessage: string;
    };
  };
}

export const AddMediaForm = ({
  uploaderContent,
  titleRequiredError,
  mediaRequiredError,
  titleLabel,
  titlePlaceholder,
  titleTooltip,
  metadataPayload,
  updateMetadata,
  showDeletePopup,
  saveChanges,
  children,
  storageSpaceByType,
  disableSaveButton,
  preUploadValidation,
}: IAddMediaFormProps) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [mediaInfo, setMediaInfo] = useState("");
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [validationData, setValidationData] = useState<
    IAddMediaFormState["validationData"]
  >({
    title: {
      invalid: false,
      validationMessage: "",
    },
    mediaInfo: {
      invalid: false,
      validationMessage: "",
    },
  });
  const isValidationChangedRef = useRef(false);
  const titleRef = useRef(title);
  const videoStorage = storageSpaceByType[MediaAssetType.Video];
  const audioStorage = storageSpaceByType[MediaAssetType.Audio];

  useEffect(() => {
    if (isValidationChangedRef.current) {
      isValidationChangedRef.current = false;
      VALIDATE.call();
    }
  });

  const triggerMediaValidationChange = (value: string) => {
    isValidationChangedRef.current = true;
    setMediaInfo(value);
    VALIDATE.call();
  };

  const changeTitle = (nextTitle: string) => {
    isValidationChangedRef.current = true;
    titleRef.current = nextTitle;
    setTitle(nextTitle);
    VALIDATE.call();
    updateMetadata({ ...metadataPayload, title: nextTitle });
  };

  const onSaveChanges = () => {
    setIsFormSubmitted(true);
    const invalidForm = Object.keys(validationData).some(
      (key) => validationData[key].invalid
    );

    if (!invalidForm) {
      saveChanges();
    }
  };

  const fileUploaded = (
    uploadedFile: IFileUploadComplete,
    durationInMs: number,
    mediaType: MediaAssetType = MediaAssetType.Video
  ) => {
    disableSaveButton(false);

    updateMetadata({
      title: titleRef.current,
      mediaInfoId: uploadedFile.id,
      mediaType,
      visible: false,
      durationInMs,
    });
  };

  const handleFileRemoved = (callback: () => void) => {
    triggerMediaValidationChange("");
    callback();
  };

  const fileRemoved = (id: string, callback: () => void) => {
    showDeletePopup(
      {
        ...new MediaMetadataPayload(),
        title,
      },
      () => handleFileRemoved(callback)
    );
  };

  const uploadStarted = (fileName: string) => {
    disableSaveButton(true);
    triggerMediaValidationChange(fileName);
  };

  const validationPerformed = (result: IGroupValidationResult) => {
    setValidationData((prevValidationData) => ({
      ...prevValidationData,
      ...result.validationData,
    }));
  };

  const isMp4FileSelected = mediaInfo.trim().toLowerCase().endsWith(".mp4");

  return (
    <>
      <div className="g-content-area c-new-media-form">
        <div className="g-col-12 g-col-lg-12 flex flex-col gap-5 md:flex-row">
          <UploadCapacityProgress
            mediaType={MediaAssetType.Video}
            usedInSec={videoStorage.usedStorageSpace.usedStoreValueInSec}
            limitInSec={videoStorage.allowedStorageSpace.maxAllowedStoreInSec}
            icon={<MediaShowreelIcon width={20} height={20} />}
          />
          <UploadCapacityProgress
            mediaType={MediaAssetType.Audio}
            usedInSec={audioStorage.usedStorageSpace.usedStoreValueInSec}
            limitInSec={audioStorage.allowedStorageSpace.maxAllowedStoreInSec}
            icon={<MicIcon width={20} height={20} />}
          />
        </div>
        <div className="g-col-md-6 g-col-lg-6 mt-10">
          <InputGroup
            className="c-new-media-form__control"
            inputConfigs={[
              {
                name: "title",
                value: title,
                validators: [requiredNonEmptyString(titleRequiredError)],
              },
              {
                name: "mediaInfo",
                value: mediaInfo,
                validators: [required(mediaRequiredError)],
              },
            ]}
            validationDone={validationPerformed}
            triggerValidation={VALIDATE}
          >
            <UploaderMapper
              content={uploaderContent}
              fileUploaded={fileUploaded}
              fileRemoved={fileRemoved}
              uploadStarted={uploadStarted}
              mediaType={MediaAssetType.Video}
              multiUpload={false}
              validationMessage={validationData.mediaInfo.validationMessage}
              invalid={isFormSubmitted && validationData.mediaInfo.invalid}
              preUploadValidation={preUploadValidation}
              accept={UPLOADER_ACCEPT_EXTENSIONS}
            />
            <Input
              id="title"
              type="text"
              value={title}
              name="title"
              data-testid={dataTestIds.profile["Media.VideoForm.TitleInput"]}
              label={titleLabel}
              placeholder={titlePlaceholder}
              valueChanged={changeTitle}
              invalid={validationData.title.invalid}
              validationMessage={validationData.title.validationMessage}
              annex={<QuestionTooltip {...titleTooltip} />}
              isFormSubmitted={isFormSubmitted}
              maxLength={TITLE_MAX_LENGTH}
            />
            {isMp4FileSelected && (
              <StatusMessage type="info" className="mt-4">
                {t("performer:media.addNew.form.mp4Info")}
              </StatusMessage>
            )}
            <div className="mt-4 rounded border border-color-mist-blue-0 bg-color-neutral-three p-4 text-base leading-[1.3] text-color-neutral-one">
              <Trans
                i18nKey="performer:media.addNew.form.processingInfo"
                components={{
                  faq: (
                    <a
                      className="text-color-brand-one"
                      aria-label={t("common:aria.label.faqs")}
                      target="_blank"
                      href="https://www.spotlight.com/help-and-faqs/managing-your-spotlight-media-library-faqs/"
                      rel="noreferrer"
                    />
                  ),
                }}
              />
            </div>
          </InputGroup>
        </div>
      </div>
      {children(onSaveChanges)}
    </>
  );
};
